import { useState, useEffect, useCallback, useRef } from 'react';

const VERSION_CHECK_INTERVAL = 30 * 60 * 1000; // Check every 30 minutes

interface BuildMeta {
  version: string;
  buildTime: string;
}

export const useVersionCheck = () => {
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const initialVersion = useRef<string | null>(null);
  const dismissed = useRef(false);

  const checkForUpdate = useCallback(async () => {
    if (dismissed.current) return;

    try {
      const response = await fetch(`/build-meta.json?t=${Date.now()}`, {
        cache: 'no-store',
      });

      if (!response.ok) return;

      const meta: BuildMeta = await response.json();

      if (!initialVersion.current) {
        initialVersion.current = meta.version;
        return;
      }

      if (meta.version !== initialVersion.current) {
        setUpdateAvailable(true);
      }
    } catch {
      // Silently fail — network issues shouldn't bother the user
    }
  }, []);

  const refresh = useCallback(() => {
    window.location.reload();
  }, []);

  const dismiss = useCallback(() => {
    dismissed.current = true;
    setUpdateAvailable(false);
  }, []);

  useEffect(() => {
    // Initial check to capture current version
    checkForUpdate();

    // Poll periodically
    const interval = setInterval(checkForUpdate, VERSION_CHECK_INTERVAL);

    // Also check when tab becomes visible
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        checkForUpdate();
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [checkForUpdate]);

  return { updateAvailable, refresh, dismiss };
};
